import type { AspirationDef } from './schema';

/**
 * Goals are concrete and mid-range. Dreams are long, identity-level, and
 * carry a regret clause: missing one does not fail the run, it colours it.
 */
const ASPIRATION_LIST: AspirationDef[] = [
  /* ---------------- goals ---------------- */
  {
    id: 'goal.emergencyFund',
    kind: 'goal',
    label: 'Build six months of runway',
    blurb: 'Enough saved that a bad year is an inconvenience, not a catastrophe.',
    category: 'finance',
    progress: { k: 'metric', path: 'runwayMonths', target: 6 },
    onAchieve: [
      { k: 'stat', stat: 'stress', delta: -7 },
      { k: 'habit', habit: 'saving', delta: 8 },
      { k: 'achievement', id: 'ach.runway', label: 'Six months of runway', category: 'finance' },
    ],
    tags: ['finance', 'security'],
  },
  {
    id: 'goal.debtFree',
    kind: 'goal',
    label: 'Clear every debt',
    blurb: 'Owe nothing to anyone.',
    category: 'finance',
    progress: { k: 'manual' },
    onAchieve: [
      { k: 'stat', stat: 'stress', delta: -10 },
      { k: 'stat', stat: 'happiness', delta: 6 },
      { k: 'achievement', id: 'ach.debtFree', label: 'Debt free', category: 'finance' },
    ],
    tags: ['finance'],
  },
  {
    id: 'goal.seniorRole',
    kind: 'goal',
    label: 'Reach a senior role',
    blurb: 'Be the person others come to for the hard calls.',
    category: 'career',
    progress: { k: 'metric', path: 'roleLevel', target: 2 },
    onAchieve: [
      { k: 'stat', stat: 'purpose', delta: 6 },
      { k: 'career', op: 'adjust', field: 'reputation', delta: 6 },
      { k: 'achievement', id: 'ach.senior', label: 'Senior in the field', category: 'career' },
    ],
    tags: ['career'],
  },
  {
    id: 'goal.fitness',
    kind: 'goal',
    label: 'Get genuinely fit',
    blurb: 'Not for a photo. For the next forty years.',
    category: 'health',
    progress: { k: 'metric', path: 'habit.exercise', target: 70 },
    onAchieve: [
      { k: 'stat', stat: 'health', delta: 8 },
      { k: 'stat', stat: 'energy', delta: 8 },
      { k: 'achievement', id: 'ach.fit', label: 'Built a body that lasts', category: 'health' },
    ],
    tags: ['health'],
  },
  {
    id: 'goal.closeFriends',
    kind: 'goal',
    label: 'Keep three real friendships',
    blurb: 'The kind you can call at 3am without explaining yourself.',
    category: 'relationship',
    progress: { k: 'metric', path: 'friendCount', target: 3 },
    onAchieve: [
      { k: 'stat', stat: 'happiness', delta: 8 },
      { k: 'achievement', id: 'ach.friends', label: 'Three real friendships', category: 'relationship' },
    ],
    tags: ['relationship'],
  },
  {
    id: 'goal.mastery',
    kind: 'goal',
    label: 'Become excellent at one thing',
    blurb: 'Depth, not breadth. Be undeniably good.',
    category: 'career',
    progress: { k: 'manual' },
    onAchieve: [
      { k: 'stat', stat: 'purpose', delta: 12 },
      { k: 'career', op: 'adjust', field: 'reputation', delta: 10 },
      { k: 'achievement', id: 'ach.mastery', label: 'Mastery', category: 'career' },
    ],
    tags: ['career', 'growth'],
  },
  {
    id: 'goal.investingHabit',
    kind: 'goal',
    label: 'Invest every single year',
    blurb: 'Boring, automatic, and the reason compounding ever works.',
    category: 'investment',
    progress: { k: 'metric', path: 'habit.investing', target: 65 },
    onAchieve: [
      { k: 'habit', habit: 'investing', delta: 6 },
      { k: 'achievement', id: 'ach.investor', label: 'A real investing habit', category: 'investment' },
    ],
    tags: ['finance'],
  },

  /* ---------------- dreams ---------------- */
  {
    id: 'dream.ownHome',
    kind: 'dream',
    label: 'Own a home',
    blurb: 'Somewhere that is yours, that no landlord can take back.',
    category: 'finance',
    progress: { k: 'flag', flag: 'ownsHome' },
    onAchieve: [
      { k: 'stat', stat: 'happiness', delta: 10 },
      { k: 'stat', stat: 'purpose', delta: 5 },
      { k: 'achievement', id: 'ach.home', label: 'Bought a home', category: 'finance' },
    ],
    onMiss: [{ k: 'regret', op: 'add', id: 'regret.home', label: 'Never owned a home', weight: 22 }],
    regretAge: 62,
    tags: ['security', 'family'],
  },
  {
    id: 'dream.family',
    kind: 'dream',
    label: 'Raise a family',
    blurb: 'Build the thing that outlasts your career.',
    category: 'family',
    progress: { k: 'flag', flag: 'hasChildren' },
    onAchieve: [
      { k: 'stat', stat: 'purpose', delta: 14 },
      { k: 'achievement', id: 'ach.family', label: 'Built a family', category: 'family' },
    ],
    onMiss: [{ k: 'regret', op: 'add', id: 'regret.family', label: 'Never started a family', weight: 30 }],
    regretAge: 52,
    tags: ['family'],
  },
  {
    id: 'dream.business',
    kind: 'dream',
    label: 'Build something of your own',
    blurb: 'Not a job. A thing with your name on it.',
    category: 'business',
    progress: { k: 'flag', flag: 'founder' },
    onAchieve: [
      { k: 'stat', stat: 'purpose', delta: 16 },
      { k: 'achievement', id: 'ach.founder', label: 'Founded something', category: 'business' },
    ],
    onMiss: [{ k: 'regret', op: 'add', id: 'regret.business', label: 'Never took the entrepreneurial leap', weight: 26 }],
    regretAge: 58,
    tags: ['entrepreneurship', 'risk'],
  },
  {
    id: 'dream.financialIndependence',
    kind: 'dream',
    label: 'Reach financial independence',
    blurb: 'Work becomes optional. That is the whole prize.',
    category: 'finance',
    progress: { k: 'metric', path: 'netWorth', target: 1200000 },
    onAchieve: [
      { k: 'stat', stat: 'stress', delta: -14 },
      { k: 'stat', stat: 'happiness', delta: 8 },
      { k: 'achievement', id: 'ach.fi', label: 'Financially independent', category: 'finance' },
    ],
    onMiss: [{ k: 'regret', op: 'add', id: 'regret.fi', label: 'Never stopped needing the paycheque', weight: 18 }],
    regretAge: 68,
    tags: ['finance', 'freedom'],
  },
  {
    id: 'dream.travel',
    kind: 'dream',
    label: 'See the world properly',
    blurb: 'Not two weeks a year. Real time, in real places.',
    category: 'life',
    progress: { k: 'metric', path: 'counter.travelTrips', target: 6 },
    onAchieve: [
      { k: 'stat', stat: 'happiness', delta: 12 },
      { k: 'trait', trait: 'adaptability', delta: 6 },
      { k: 'achievement', id: 'ach.travel', label: 'Saw the world', category: 'life' },
    ],
    onMiss: [{ k: 'regret', op: 'add', id: 'regret.travel', label: 'Kept postponing the trip', weight: 16 }],
    regretAge: 66,
    tags: ['exploration', 'leisure'],
  },
  {
    id: 'dream.craft',
    kind: 'dream',
    label: 'Make something that matters',
    blurb: 'A book, a body of work, a thing that exists because you insisted.',
    category: 'life',
    progress: { k: 'metric', path: 'counter.creativeWorks', target: 3 },
    onAchieve: [
      { k: 'stat', stat: 'purpose', delta: 18 },
      { k: 'achievement', id: 'ach.craft', label: 'Made something that lasts', category: 'life' },
    ],
    onMiss: [{ k: 'regret', op: 'add', id: 'regret.craft', label: 'Never finished the work that mattered', weight: 24 }],
    regretAge: 64,
    tags: ['creative', 'meaning'],
  },
  {
    id: 'dream.contribute',
    kind: 'dream',
    label: 'Leave people better off',
    blurb: 'Mentor, give, build capacity in others.',
    category: 'legacy',
    progress: { k: 'metric', path: 'counter.contributions', target: 5 },
    onAchieve: [
      { k: 'stat', stat: 'purpose', delta: 16 },
      { k: 'achievement', id: 'ach.contribute', label: 'Left people better off', category: 'legacy' },
    ],
    onMiss: [{ k: 'regret', op: 'add', id: 'regret.contribute', label: 'Meant to give back, and didn’t', weight: 20 }],
    regretAge: 70,
    tags: ['contribution', 'legacy'],
  },
  {
    id: 'dream.earlyRetire',
    kind: 'dream',
    label: 'Retire before sixty',
    blurb: 'Buy back the decade most people spend working.',
    category: 'finance',
    progress: { k: 'manual' },
    onAchieve: [
      { k: 'stat', stat: 'happiness', delta: 10 },
      { k: 'achievement', id: 'ach.earlyRetire', label: 'Retired early', category: 'finance' },
    ],
    onMiss: [{ k: 'regret', op: 'add', id: 'regret.earlyRetire', label: 'Worked longer than planned', weight: 12 }],
    regretAge: 62,
    tags: ['finance', 'freedom'],
  },
  {
    id: 'dream.partnership',
    kind: 'dream',
    label: 'Build a partnership that lasts',
    blurb: 'One person, decades deep, still choosing each other.',
    category: 'relationship',
    progress: { k: 'manual' },
    onAchieve: [
      { k: 'stat', stat: 'happiness', delta: 14 },
      { k: 'achievement', id: 'ach.partnership', label: 'A partnership that lasted', category: 'relationship' },
    ],
    onMiss: [{ k: 'regret', op: 'add', id: 'regret.partnership', label: 'Never found a lasting partnership', weight: 22 }],
    regretAge: 60,
    tags: ['relationship'],
  },
];

export const ASPIRATIONS: Record<string, AspirationDef> = Object.fromEntries(
  ASPIRATION_LIST.map((a) => [a.id, a]),
);

export const ASPIRATION_DEF_LIST: readonly AspirationDef[] = ASPIRATION_LIST;
